import React, {useCallback, useEffect, useState} from 'react'
import styles from './UIGameVote.module.scss'
import {VoteState} from '@colonist/game-state-types'
import clsx from 'clsx'
import {T} from '@colonist/ui'
import {UIGameTimerBar} from '../UIGameTimerBar/UIGameTimerBar'
import {PlayerColor} from '@colonist/game-states'
import {UIGameVoteParticipants} from './UIGameVoteParticipants'
import {UIGameVoteActions} from './UIGameVoteActions'
import {Sound} from '../../utils/sound'
import IconX from '@static/images/icon_x.svg'
import {useGameStore} from '../../store/GameStoreContext'

export interface UIGameVoteProps {
    voteState: VoteState
    onVoteAccept: (voteId: number) => void
    onVoteReject: (voteId: number) => void
    onHideVote: () => void
    className?: string | undefined
}

export const UIGameVote = ({
    voteState,
    onVoteAccept,
    onVoteReject,
    onHideVote,
    className,
}: UIGameVoteProps) => {
    const initialTimeLeftMs = voteState.duration - voteState.timePassed
    const [timeLeftMs, setTimeLeftMs] = useState(initialTimeLeftMs)
    const myColor = useGameStore((state) => state.myColor)
    const amIAbleToVote = voteState.participatingPlayers.includes(myColor)

    useEffect(() => {
        Sound.playSound(Sound.SoundType.VoteReminder)
    }, [])

    useEffect(() => {
        setTimeLeftMs(initialTimeLeftMs)
        if(timeLeftMs <= 0) return

        const interval = setInterval(() => {
            setTimeLeftMs((prev) => {
                const next = prev - 100
                if(next <= 0) {
                    clearInterval(interval)
                    return 0
                }
                return next
            })
        }, 100)

        return () => clearInterval(interval)
    }, [initialTimeLeftMs])

    const timeLeftPercentage = Math.max(0, Math.min(1, timeLeftMs / voteState.duration)) * 100

    const onAccept = useCallback(() => {
        onVoteAccept(voteState.id)
    }, [onVoteAccept, voteState.id])

    const onReject = useCallback(() => {
        onVoteReject(voteState.id)
    }, [onVoteReject, voteState.id])

    return (
        <div className={clsx(styles.container, className)}>
            <img src={IconX} className={styles.closeButton} onClick={onHideVote} />
            <div>
                <div className={styles.title}><T id={voteState.title} /></div>
                <div className={styles.description}>
                    <T id={{
                        key: 'strings:game.vote.playersRequiredForVoteToPass',
                        options: {
                            count: voteState.requiredNumberOfAcceptVotesToPass,
                        },
                    }} />
                </div>
            </div>

            <UIGameTimerBar
                isAnimated
                timeLeftPercentage={timeLeftPercentage}
                className={styles.timerBar}
                currentTurnPlayerColor={PlayerColor.Orange}
            />
            <UIGameVoteParticipants
                acceptedVotes={voteState.acceptedVotes}
                declinedVotes={voteState.declinedVotes}
                participatingPlayers={voteState.participatingPlayers}
            />
            {amIAbleToVote && <UIGameVoteActions
                acceptedVotes={voteState.acceptedVotes}
                declinedVotes={voteState.declinedVotes}
                onAccept={onAccept}
                onDecline={onReject}
            />}
        </div>
    )
}
